import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { createRequesterHelpdeskRuntime, HelpdeskRuntimeProvider } from '@features/helpdesk/embed';
import type { HelpdeskWidgetConfiguration } from './model/config';
import { createWidgetI18n } from './model/i18n';
import { HelpdeskWidget } from './ui/HelpdeskWidget';
import { HelpdeskStandalone } from './ui/HelpdeskStandalone';
import { installWidgetFonts } from './model/fonts';
import { WidgetSession, type WidgetSessionSnapshot } from './model/widget-session';
import { BubbleSession, type BubbleSnapshot } from './model/bubble-session';

export interface HelpdeskWidgetHandle {
  destroy(): void;
}

interface RuntimeOptions {
  expectedAccount?: boolean;
  recoverSession?: () => Promise<void>;
}

const WIDGET_REM_PIXELS = 16;
const GUEST_SNAPSHOT: WidgetSessionSnapshot = { status: 'guest', generation: 0 };
const GUEST_BUBBLE_SNAPSHOT: BubbleSnapshot = { status: 'guest', generation: 0, epoch: 0 };
const subscribeGuest = () => () => undefined;
const getGuestSnapshot = () => GUEST_SNAPSHOT;
const getGuestBubbleSnapshot = () => GUEST_BUBBLE_SNAPSHOT;

function isolatedStyles(styles: string): string {
  // rem follows the embedding document's html font size, outside the shadow root.
  return styles
    .replace(/:root\b/g, ':host')
    .replace(
      /([;{]\s*)([-\w]+)(\s*:\s*)([^;{}]+)(?=[;}])/g,
      (_declaration, start: string, property: string, separator: string, value: string) =>
        start +
        property +
        separator +
        value.replace(
          /(-?\d*\.?\d+)rem\b/g,
          (_unit, size: string) => `${String(Number(size) * WIDGET_REM_PIXELS)}px`
        )
    );
}

/** A single page installation, isolated from host styles, queries and locale state. */
export function mountHelpdeskWidget(
  configuration: HelpdeskWidgetConfiguration,
  styles: string,
  configurationError?: string,
  runtimeOptions: RuntimeOptions = {}
): HelpdeskWidgetHandle | null {
  if (document.querySelector('[data-move-helpdesk-widget]') !== null) return null;
  const host = document.createElement('move-helpdesk-widget');
  host.setAttribute('data-move-helpdesk-widget', '');
  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  const fonts = installWidgetFonts();
  // Preserve canonical token values while isolating their selector and reference font unit.
  style.textContent =
    ':host{all:initial;position:relative;z-index:2147483000}' + isolatedStyles(styles);
  const container = document.createElement('div');
  shadow.append(style, container);
  document.body.append(host);
  return mountHelpdeskSurface({
    container,
    configuration,
    configurationError,
    runtimeOptions,
    presentation: 'popup',
    removeHost: () => {
      host.remove();
      fonts.remove();
    },
  });
}

/** Use the SPA's styles with the same isolated requester lifecycle as the external popup. */
export function mountHelpdeskPage(
  target: HTMLElement,
  configuration: HelpdeskWidgetConfiguration,
  runtimeOptions: RuntimeOptions = {}
): HelpdeskWidgetHandle {
  const container = document.createElement('div');
  target.append(container);
  return mountHelpdeskSurface({
    container,
    configuration,
    runtimeOptions,
    presentation: 'page',
    removeHost: () => {
      container.remove();
    },
  });
}

function mountHelpdeskSurface({
  container,
  configuration,
  configurationError,
  runtimeOptions,
  presentation,
  removeHost,
}: {
  container: HTMLElement;
  configuration: HelpdeskWidgetConfiguration;
  configurationError?: string;
  runtimeOptions: RuntimeOptions;
  presentation: 'popup' | 'page';
  removeHost: () => void;
}): HelpdeskWidgetHandle {
  const client = new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: 0 } } });
  const external = new URL(configuration.supportUrl).origin !== window.location.origin;
  const session = external && !configurationError ? new WidgetSession(configuration.apiBase) : null;
  const bubble = session && configuration.bubbleIdentity ? new BubbleSession(session) : null;
  const actorKey = () => session?.hasAccountActor()
    ? `move:${String(session.snapshot().generation)}`
    : bubble?.hasAccountActor()
      ? `bubble:${String(bubble.snapshot().generation)}`
      : `guest:${String(session?.snapshot().generation ?? 0)}:${String(bubble?.snapshot().generation ?? 0)}`;
  let currentActorKey = actorKey();
  const clearChangedActor = () => {
    const next = actorKey();
    if (next === currentActorKey) return;
    currentActorKey = next;
    void client.cancelQueries();
    client.clear();
  };
  const unsubscribe = session?.subscribe(clearChangedActor);
  const unsubscribeBubble = bubble?.subscribe(clearChangedActor);
  const locale = createWidgetI18n(configuration.locale);
  const root = createRoot(container);
  root.render(
    <I18nextProvider i18n={locale}>
      <QueryClientProvider client={client}>
        <MountedSurface configuration={configuration} configurationError={configurationError}
          runtimeOptions={runtimeOptions} presentation={presentation} session={session} bubble={bubble} />
      </QueryClientProvider>
    </I18nextProvider>
  );
  let disposed = false;
  return {
    destroy() {
      if (disposed) return;
      disposed = true;
      unsubscribe?.();
      unsubscribeBubble?.();
      bubble?.dispose();
      session?.dispose();
      void client.cancelQueries();
      client.clear();
      removeHost();
      // A Move auth change can run during another React root's layout commit.
      // Remove access and visible state now; unmount that independent root after the commit.
      queueMicrotask(() => {
        root.unmount();
      });
    },
  };
}

function MountedSurface({ configuration, configurationError, runtimeOptions, presentation, session, bubble }: {
  configuration: HelpdeskWidgetConfiguration;
  configurationError?: string;
  runtimeOptions: RuntimeOptions;
  presentation: 'popup' | 'page';
  session: WidgetSession | null;
  bubble: BubbleSession | null;
}) {
  const snapshot = useSyncExternalStore(
    session?.subscribe ?? subscribeGuest,
    session?.snapshot ?? getGuestSnapshot
  );
  const bubbleSnapshot = useSyncExternalStore(
    bubble?.subscribe ?? subscribeGuest,
    bubble?.snapshot ?? getGuestBubbleSnapshot
  );
  const actorKey = session?.hasAccountActor()
    ? `move:${String(snapshot.generation)}`
    : bubble?.hasAccountActor()
      ? `bubble:${String(bubbleSnapshot.generation)}`
      : `guest:${String(snapshot.generation)}:${String(bubbleSnapshot.generation)}`;
  const runtime = useMemo(() => createRequesterHelpdeskRuntime({
    apiBase: configuration.apiBase,
    source: configuration.source,
    scope: `${crypto.randomUUID()}-${actorKey}`,
    ...runtimeOptions,
    ...(session?.hasAccountActor() ? {
      expectedAccount: true,
      bearer: { getAccessToken: () => session.getAccessToken(), onRejected: () => { session.reject(); },
        generation: () => session.snapshot().generation },
    } : bubble?.hasAccountActor() ? {
      expectedAccount: true,
      bearer: { getAccessToken: () => bubble.getAccessToken(), onRejected: () => { bubble.reject(); },
        generation: () => bubble.snapshot().epoch },
    } : {}),
  }), [configuration.apiBase, configuration.source, runtimeOptions, session, bubble, actorKey]);
  useEffect(() => () => { runtime.dispose(); }, [runtime]);
  return <HelpdeskRuntimeProvider runtime={runtime}>
    {presentation === 'page' ?
      <HelpdeskStandalone configuration={configuration} runtime={runtime} /> :
      <HelpdeskWidget configuration={configuration} runtime={runtime}
        configurationError={configurationError} session={session} sessionSnapshot={snapshot}
        bubble={bubble} bubbleSnapshot={bubbleSnapshot} actorKey={actorKey} />}
  </HelpdeskRuntimeProvider>;
}
